import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Calendar, TrendingUp, AlertTriangle } from 'lucide-react';
import { http } from '@/lib/http';
import { toast } from 'sonner';

interface DailyStat {
  providerId: string;
  date: string;
  totalRequests: number;
  weightedTokens: number;
}

interface LimitStatusDetails {
  usage: number;
  limit: number;
  status: 'ok' | 'warning' | 'exceeded';
  windowHours?: number;
}

interface ProviderLimitStatus {
  daily?: LimitStatusDetails;
  rolling?: LimitStatusDetails;
  status?: 'unlimited';
}

interface ProviderLimits {
  requestsPerDay?: number;
  requestsPerFiveHours?: number;
  sizeBasedMultiplier?: boolean;
  minTokens?: number;
  maxTokens?: number;
  minMultiplier?: number;
  maxMultiplier?: number;
  unlimited?: boolean;
}

export default function UsageStatsPage() {
  const [stats, setStats] = useState<DailyStat[]>([]);
  const [limits, setLimits] = useState<Record<string, ProviderLimits>>({});
  const [limitStatuses, setLimitStatuses] = useState<Record<string, ProviderLimitStatus>>({});
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [days, setDays] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [selectedProvider, days]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await http<{ dailyStats: DailyStat[], limits: Record<string, ProviderLimits>, limitStatuses: Record<string, ProviderLimitStatus> }>(`/api/usage/stats?days=${days}`, {
        method: 'GET',
      });
      setStats(response.dailyStats);
      setLimits(response.limits);
      setLimitStatuses(response.limitStatuses);
    } catch (error: any) {
      toast.error("Failed to fetch usage stats", { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const filteredStats = stats.filter(stat => 
    selectedProvider === 'all' || stat.providerId === selectedProvider
  );

  const chartData = filteredStats.map(stat => ({
    date: days <= 1
      ? new Date(stat.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      : new Date(stat.date).toLocaleDateString(),
    requests: stat.totalRequests,
    tokens: Math.round(stat.weightedTokens / 1000), // Convert to thousands
  }));

  const getStatusVariant = (status: 'ok' | 'warning' | 'exceeded' | 'unlimited' | undefined) => {
    if (status === 'exceeded') return 'destructive';
    if (status === 'warning') return 'secondary';
    if (status === 'unlimited') return 'outline';
    return 'default';
  };

  // Create a comprehensive list of all providers to display.
  // This includes providers with usage and providers with configured limits.
  const providersWithUsage = new Set(stats.map(s => s.providerId));
  const providersWithLimits = new Set(Object.keys(limits));
  const allProviderIds = Array.from(new Set([...providersWithUsage, ...providersWithLimits])).sort();

  const providerSummaryData = allProviderIds.map(providerId => {
    const providerStats = stats.filter(s => s.providerId === providerId);
    const totalRequests = providerStats.reduce((sum, s) => sum + s.totalRequests, 0);
    const totalTokens = providerStats.reduce((sum, s) => sum + s.weightedTokens, 0);
    return {
      providerId,
      totalRequests,
      totalTokens: Math.round(totalTokens / 1000), // in thousands
      status: limitStatuses[providerId] // This may be undefined for providers with usage but no limits
    };
  });

  return (
    <div className="container mx-auto py-4 sm:py-8 px-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><TrendingUp /> Provider Usage Statistics</h1>
        <Button onClick={fetchStats} disabled={loading}>
          <Calendar className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Providers</SelectItem>
                  {Array.from(new Set(stats.map(s => s.providerId))).map(providerId => (
                    <SelectItem key={providerId} value={providerId}>{providerId}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={days.toString()} onValueChange={(value) => setDays(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provider Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {allProviderIds.map(providerId => {
          const providerStatus = limitStatuses[providerId];
          const limitInfo = limits[providerId];
          return (
            <Card key={providerId}>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg capitalize">{providerId}</CardTitle>
                <CardDescription>
                  {limitInfo?.unlimited ? 'Unlimited usage' : (providerStatus ? 'Request limit usage' : 'No limits configured')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {providerStatus?.status === 'unlimited' && (
                  <Badge variant="outline">Unlimited</Badge>
                )}
                {providerStatus?.daily && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant={getStatusVariant(providerStatus.daily.status)} className="w-20 justify-center">
                        {providerStatus.daily.status === 'exceeded' && <AlertTriangle className="w-3 h-3 mr-1" />}
                        {providerStatus.daily.status}
                      </Badge>
                      <span className="font-medium text-muted-foreground">Daily</span>
                    </div>
                    <div className="font-mono text-xs">
                      {providerStatus.daily.usage} / {providerStatus.daily.limit}
                    </div>
                  </div>
                )}
                {providerStatus?.rolling && (
                   <div className="flex items-center justify-between">
                     <div className="flex items-center gap-2">
                       <Badge variant={getStatusVariant(providerStatus.rolling.status)} className="w-20 justify-center">
                         {providerStatus.rolling.status === 'exceeded' && <AlertTriangle className="w-3 h-3 mr-1" />}
                         {providerStatus.rolling.status}
                       </Badge>
                       <span className="font-medium text-muted-foreground">Rolling ({providerStatus.rolling.windowHours}h)</span>
                     </div>
                     <div className="font-mono text-xs">
                       {providerStatus.rolling.usage} / {providerStatus.rolling.limit}
                     </div>
                   </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary Table */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Provider Summary</CardTitle>
          <CardDescription>
            Usage and limit overview for the selected period ({days === 1 ? 'Last 24 hours' : `Last ${days} days`}).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead className="text-right">Requests</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">Tokens (K)</TableHead>
                  <TableHead className="hidden md:table-cell">Daily Limit</TableHead>
                  <TableHead className="hidden lg:table-cell">Rolling Limit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providerSummaryData.map(summary => (
                  <TableRow key={summary.providerId}>
                    <TableCell className="font-medium capitalize">{summary.providerId}</TableCell>
                    <TableCell className="text-right">{summary.totalRequests}</TableCell>
                    <TableCell className="text-right hidden sm:table-cell">{summary.totalTokens}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {summary.status?.status === 'unlimited' ? (
                        <Badge variant="outline">Unlimited</Badge>
                      ) : summary.status?.daily ? (
                        <div className="flex items-center gap-2">
                          <Badge variant={getStatusVariant(summary.status.daily.status)}>
                            {summary.status.daily.status}
                          </Badge>
                          <span className="text-sm text-muted-foreground hidden lg:inline">{summary.status.daily.usage} / {summary.status.daily.limit}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">{summary.status ? 'N/A' : 'No limits set'}</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {summary.status?.status === 'unlimited' ? (
                         <Badge variant="outline">Unlimited</Badge>
                      ) : summary.status?.rolling ? (
                        <div className="flex items-center gap-2">
                          <Badge variant={getStatusVariant(summary.status.rolling.status)}>
                            {summary.status.rolling.status}
                          </Badge>
                          <span className="text-sm text-muted-foreground hidden xl:inline">{summary.status.rolling.usage} / {summary.status.rolling.limit}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">{summary.status ? 'N/A' : 'No limits set'}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Request Count Over Time</CardTitle>
            <CardDescription>Number of requests per day</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250} className="min-h-[200px]">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }} 
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={chartData.length > 10 ? Math.floor(chartData.length / 5) : 0}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="requests" stroke="#8884d8" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Token Usage Over Time</CardTitle>
            <CardDescription>Weighted tokens (in thousands)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250} className="min-h-[200px]">
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }} 
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={chartData.length > 10 ? Math.floor(chartData.length / 5) : 0}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="tokens" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}